import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  User,
  LogOut,
  Shield,
  Bell,
  Star,
  Plus,
  Route,
  CarFront,
  LayoutDashboard,
  Calendar,
  Home,
  Info,
  Mail,
  HelpCircle,
} from 'lucide-react';
import { User as UserType } from '../../../features/auth/types/user';
import { NavItem } from './types';

interface MobileMenuContentProps {
  isAuthenticated: boolean;
  user: UserType | null;
  mainNavItems: NavItem[];
  setMobileMenuOpen: (open: boolean) => void;
  handleSignIn: () => void;
  logout: () => void;
}

export const MobileMenuContent: React.FC<MobileMenuContentProps> = ({
  isAuthenticated,
  user,
  setMobileMenuOpen,
  handleSignIn,
  logout,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  if (!isAuthenticated) {
    return (
      <>
        {/* Navigation Pages Section */}
        <div className="flex flex-col gap-0">
          <div className="px-3 py-1">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Explore</h3>
          </div>
          <button
            onClick={() => {
              navigate('/');
              setMobileMenuOpen(false);
            }}
            className={`w-full text-left px-3 py-3 text-base font-medium flex items-center gap-3 rounded-lg transition-colors ${
              location.pathname === '/' 
                ? 'bg-primary-50 text-primary-700 border border-primary-200' 
                : 'hover:bg-gray-50'
            }`}
          >
            <Home className="w-5 h-5" />
            <span>Home</span>
          </button>
          <button
            onClick={() => {
              navigate('/safaris-search');
              setMobileMenuOpen(false);
            }}
            className={`w-full text-left px-3 py-3 text-base font-medium flex items-center gap-3 rounded-lg transition-colors ${
              location.pathname === '/safaris-search' 
                ? 'bg-primary-50 text-primary-700 border border-primary-200' 
                : 'hover:bg-gray-50'
            }`}
          >
            <Route className="w-5 h-5" />
            <span>Safaris</span>
          </button>
          <button
            onClick={() => {
              navigate('/transport-search');
              setMobileMenuOpen(false);
            }}
            className={`w-full text-left px-3 py-3 text-base font-medium flex items-center gap-3 rounded-lg transition-colors ${
              location.pathname === '/transport-search' 
                ? 'bg-primary-50 text-primary-700 border border-primary-200' 
                : 'hover:bg-gray-50'
            }`}
          >
            <CarFront className="w-5 h-5" />
            <span>Trips</span>
          </button>
        </div>

        {/* Fading Divider */}
        <div className="relative my-1">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center">
            <div className="bg-white/90 px-2">
              <div className="h-px w-6 bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
            </div>
          </div>
        </div>

        {/* Public Links Section */}
        <div className="flex flex-col gap-0">
          <div className="px-3 py-1">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Company</h3>
          </div>
          <button
            onClick={() => {
              navigate('/about');
              setMobileMenuOpen(false);
            }}
            className="w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Info className="w-4 h-4" />
            <span>About Us</span>
          </button>
          <button
            onClick={() => {
              navigate('/contact');
              setMobileMenuOpen(false);
            }}
            className="w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Mail className="w-4 h-4" />
            <span>Contact</span>
          </button>
          <button
            onClick={() => {
              navigate('/help');
              setMobileMenuOpen(false);
            }}
            className="w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <HelpCircle className="w-4 h-4" />
            <span>Help Center</span>
          </button>
        </div>

        {/* Fading Divider */}
        <div className="relative my-1">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center">
            <div className="bg-white/90 px-2">
              <div className="h-px w-6 bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
            </div>
          </div>
        </div>

        {/* Auth Section */}
        <div className="flex flex-col gap-1">
          <button
            onClick={() => {
              setMobileMenuOpen(false);
              handleSignIn();
            }}
            className="w-full text-left px-3 py-3 text-base font-medium flex items-center gap-3 rounded-lg hover:bg-primary-50/80 transition-colors border border-primary-100"
          >
            <User className="w-5 h-5" />
            <span>Sign In</span>
          </button>
          <button
            onClick={() => {
              setMobileMenuOpen(false);
              navigate('/register');
            }}
            className="w-full text-left px-3 py-3 text-base font-medium flex items-center gap-3 bg-primary-500 text-white hover:bg-primary-600 transition-colors rounded-lg shadow-soft"
          >
            <Plus className="w-5 h-5" />
            <span>Get Started</span>
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      {/* User Profile Section for Mobile */}
      <div className="px-3 py-2 mb-1">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
            <span className="text-white font-semibold text-xs">
              {user?.first_name?.charAt(0)?.toUpperCase() || user?.last_name?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div>
            <p className="font-medium text-gray-900 text-sm">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-xs text-gray-500">
              {user?.type === 'operator' ? user?.operator_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : user?.type}
            </p>
          </div>
        </div>
      </div>
      
      {/* Dashboard Pages Section */}
      <div className="flex flex-col gap-0">
        <div className="px-3 py-1">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Dashboard</h3>
        </div>
        <button
          onClick={() => {
            navigate('/dashboard');
            setMobileMenuOpen(false);
          }}
          className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
            location.pathname === '/dashboard' 
              ? 'bg-primary-50 text-primary-700 border border-primary-200' 
              : 'hover:bg-gray-50'
          }`}
        >
          <LayoutDashboard className="w-4 h-4" />
          <span>Overview</span>
        </button>
        <button
          onClick={() => {
            navigate('/bookings');
            setMobileMenuOpen(false);
          }}
          className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
            location.pathname === '/bookings' 
              ? 'bg-primary-50 text-primary-700 border border-primary-200' 
              : 'hover:bg-gray-50'
          }`}
        >
          <Calendar className="w-4 h-4" />
          <span>Bookings</span>
        </button>
        {user?.type === 'operator' && user?.operator_type === 'transporter' && (
          <>
            <button
              onClick={() => {
                navigate('/trips');
                setMobileMenuOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
                location.pathname === '/trips' 
                  ? 'bg-primary-50 text-primary-700 border border-primary-200' 
                  : 'hover:bg-gray-50'
              }`}
            >
              <CarFront className="w-4 h-4" />
              <span>Trips</span>
            </button>
            <button
              onClick={() => {
                navigate('/fleet');
                setMobileMenuOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
                location.pathname === '/fleet' 
                  ? 'bg-primary-50 text-primary-700 border border-primary-200' 
                  : 'hover:bg-gray-50'
              }`}
            >
              <CarFront className="w-4 h-4" />
              <span>Fleet</span>
            </button>
          </>
        )}
        {user?.type === 'operator' && user?.operator_type === 'tour_operator' && (
          <button
            onClick={() => {
              navigate('/safaris');
              setMobileMenuOpen(false);
            }}
            className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
              location.pathname === '/safaris' 
                ? 'bg-primary-50 text-primary-700 border border-primary-200' 
                : 'hover:bg-gray-50'
            }`}
          >
            <Route className="w-4 h-4" />
            <span>Safaris</span>
          </button>
        )}
      </div>

      {/* Fading Divider */}
      <div className="relative my-1">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200"></div>
        </div>
        <div className="relative flex justify-center">
          <div className="bg-white/90 px-2">
            <div className="h-px w-6 bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
          </div>
        </div>
      </div>

      {/* Account & Settings Section */}
      <div className="flex flex-col gap-0">
        <div className="px-3 py-1">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Account</h3>
        </div>
        <button
          onClick={() => {
            navigate('/account');
            setMobileMenuOpen(false);
          }}
          className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
            location.pathname === '/account' 
              ? 'bg-primary-50 text-primary-700 border border-primary-200' 
              : 'hover:bg-gray-50'
          }`}
        >
          <User className="w-4 h-4" />
          <span>Settings</span>
        </button>
        <button
          onClick={() => {
            navigate('/notifications');
            setMobileMenuOpen(false);
          }}
          className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
            location.pathname === '/notifications' 
              ? 'bg-primary-50 text-primary-700 border border-primary-200' 
              : 'hover:bg-gray-50'
          }`}
        >
          <Bell className="w-4 h-4" />
          <span>Notifications</span>
        </button>
        {user?.type === 'operator' && (
          <>
            <button
              onClick={() => {
                navigate('/reviews');
                setMobileMenuOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
                location.pathname === '/reviews' 
                  ? 'bg-primary-50 text-primary-700 border border-primary-200' 
                  : 'hover:bg-gray-50'
              }`}
            >
              <Star className="w-4 h-4" />
              <span>Reviews</span>
            </button>
            <button
              onClick={() => {
                navigate('/verification-required');
                setMobileMenuOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg transition-colors ${
                location.pathname === '/verification-required' 
                  ? 'bg-primary-50 text-primary-700 border border-primary-200' 
                  : 'hover:bg-gray-50'
              }`}
            >
              <Shield className="w-4 h-4" />
              <span>Verification</span>
            </button>
          </>
        )}
      </div>

      {/* Fading Divider */}
      <div className="relative my-1">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200"></div>
        </div>
        <div className="relative flex justify-center">
          <div className="bg-white/90 px-2">
            <div className="h-px w-6 bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
          </div>
        </div>
      </div>

      {/* Sign Out */}
      <button
        onClick={() => {
          logout();
          setMobileMenuOpen(false);
        }}
        className="w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 rounded-lg hover:bg-red-50 transition-colors text-red-600"
      >
        <LogOut className="w-4 h-4" />
        <span>Sign Out</span>
      </button>
    </>
  );
}; 